import { LegalPage } from "./Legal.js";

/**
 * The privacy policy.
 *
 * Written from what the code does rather than from a template: every claim
 * below is checkable against `server/src/scopes.ts`, `server/src/crypto.ts` and
 * `server/prisma/schema.prisma`. If one of those changes so that a sentence
 * here stops being true, this page is part of the change.
 *
 * The bracketed placeholders are the legal identity of whoever operates the
 * service, which is not something the code knows. They must be filled in before
 * this is published - an unfilled placeholder is worse than no page.
 */
export function Privacy() {
  return (
    <LegalPage title="Privacy" updated="14 September 2026">
      <p className="lede">
        Meet With reads your calendars so that people you invite can see when
        you&rsquo;re free. It never writes to them, and it never shows a visitor what
        you&rsquo;re doing — only whether a time is open.
      </p>

      <h2>Who is responsible</h2>
      <p>
        Meet With is operated by <strong>[legal entity name]</strong>, of{" "}
        <strong>[registered address]</strong>. For anything on this page, write to{" "}
        <a href="mailto:privacy@meetwith.org">privacy@meetwith.org</a>.
      </p>
      <p>
        If you are an owner, you are responsible for the addresses you invite. If you
        were invited, the person who invited you decides what you can see.
      </p>

      <h2>What we access, and why</h2>
      <p>
        When you connect a calendar, we ask for the narrowest access that makes the
        product work, and only ever read access:
      </p>
      <ul>
        <li>
          <strong>Microsoft 365 / Entra ID:</strong> <code>Calendars.ReadBasic</code> —
          the start and end times of events, and the names of your calendars. If you
          turn on <em>Titles too</em>, this becomes <code>Calendars.Read</code>, which
          also returns event subjects. Plus <code>openid</code>, <code>profile</code>,{" "}
          <code>email</code> and <code>offline_access</code> to identify the account
          and keep the connection alive.
        </li>
        <li>
          <strong>Google:</strong>{" "}
          <code>https://www.googleapis.com/auth/calendar.readonly</code>, plus{" "}
          <code>openid</code> and <code>email</code> so we know which account was
          connected.
        </li>
        <li>
          <strong>Calendar subscriptions:</strong> if you add a published feed link, we
          fetch it on a schedule. The link is the only credential involved, and we treat
          it as one.
        </li>
      </ul>
      <p>
        We never request permission to create, change, move or delete an event, and
        never permission to send mail from your mailbox. Those scopes are refused in
        code rather than merely avoided by habit. We do not read event bodies,
        attachments, attendees, or anything in your mailbox.
      </p>

      <h2>What we store</h2>
      <ul>
        <li>
          <strong>Your account:</strong> name, email address, the name shown to
          visitors, timezone, and which sign-in provider you used.
        </li>
        <li>
          <strong>Connected accounts:</strong> the account email, the organisation
          label, the Microsoft tenant identifier where applicable, which scopes were
          granted, and an encrypted refresh token.
        </li>
        <li>
          <strong>Busy times:</strong> start and end times of events on the calendars
          you selected, with all-day flags and the provider&rsquo;s event identifiers.
          Event <em>titles</em> are stored only if you turned on <em>Titles too</em>,
          and are never sent to a visitor. This is a cache: it is replaced wholesale
          each time we read a calendar.
        </li>
        <li>
          <strong>Your settings:</strong> working hours, buffers, notice, booking
          window, meeting lengths, and the blackouts you named.
        </li>
        <li>
          <strong>Invitations and requests:</strong> the addresses you invited, a
          visitor&rsquo;s name if they gave one, what a request said it was about, any
          note about flexibility, and the times involved.
        </li>
        <li>
          <strong>Operational records:</strong> which messages we sent and whether they
          were delivered, and a log of significant actions which may include an IP
          address.
        </li>
      </ul>

      <h2>How Google user data is used</h2>
      <p>
        Meet With&rsquo;s use of information received from Google APIs adheres to the{" "}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. Concretely:
      </p>
      <ul>
        <li>
          Google calendar data is used only to work out when you are free and to show
          that to people you invited.
        </li>
        <li>We do not use it for advertising, and we do not sell it.</li>
        <li>
          We do not use it to train generalised artificial intelligence or machine
          learning models.
        </li>
        <li>
          No human reads it, except where you have asked us to, where it is necessary
          for security purposes, or where the law requires it.
        </li>
        <li>
          We do not transfer it to others except as needed to run the service, for
          security, or to comply with the law.
        </li>
      </ul>

      <h2>What visitors see</h2>
      <p>
        A visitor sees times and nothing else: available or not available. They are
        never shown event titles, attendees, which calendar a busy time came from, how
        many calendars you have connected, or why any particular time is missing. A
        meeting, a buffer, a blackout and a public holiday are indistinguishable to
        them — each is simply not offered.
      </p>

      <h2>Who else is involved</h2>
      <p>
        We do not sell personal data and we do not share it for advertising. We use
        service providers to run Meet With — hosting and database, email delivery, and
        the calendar providers you connect. They process data on our instructions and
        for no other purpose. We may disclose data where the law requires it.
      </p>

      <h2>How it is protected</h2>
      <ul>
        <li>
          Refresh tokens and subscription links are encrypted before they are stored,
          using AES-256-GCM. They are never returned by the API and never logged.
        </li>
        <li>
          Session and invitation tokens are stored only as SHA-256 hashes; the tokens
          themselves exist only in your browser and in the email we sent.
        </li>
        <li>Visitor confirmation codes are hashed with argon2 and attempt-capped.</li>
        <li>Everything is served over TLS.</li>
      </ul>

      <h2>How long it is kept</h2>
      <ul>
        <li>
          <strong>Disconnecting a calendar</strong> deletes that account, its stored
          refresh token, and every busy time cached from it, immediately.
        </li>
        <li>
          <strong>Revoking an invitation</strong> ends that visitor&rsquo;s sessions
          immediately and stops any further access.
        </li>
        <li>
          <strong>Busy times</strong> are replaced on each read and never accumulate.
        </li>
        <li>
          <strong>Deleting your account</strong> removes everything above. There is no
          self-service button for this yet — write to{" "}
          <a href="mailto:privacy@meetwith.org">privacy@meetwith.org</a> and we will
          delete your account and all associated data within 30 days, and confirm when
          it is done.
        </li>
      </ul>

      <h2>Cookies</h2>
      <p>
        Three, all strictly functional. <code>mw_session</code> keeps an owner signed
        in, <code>mw_visitor</code> keeps a confirmed visitor signed in, and{" "}
        <code>mw_oauth</code> exists for a few minutes during sign-in to protect the
        exchange. There are no analytics, advertising, or tracking cookies, and no
        third-party scripts.
      </p>

      <h2>Your rights</h2>
      <p>
        You can ask what we hold about you, ask us to correct it, ask us to delete it,
        or object to our handling of it. Write to{" "}
        <a href="mailto:privacy@meetwith.org">privacy@meetwith.org</a>. Depending on
        where you live you may also have the right to complain to a data protection
        authority.
      </p>
      <p>
        You can withdraw calendar access at any time, either by disconnecting the
        account in Meet With or from your provider directly — in{" "}
        <a
          href="https://myaccount.google.com/permissions"
          target="_blank"
          rel="noreferrer"
        >
          Google account permissions
        </a>{" "}
        or{" "}
        <a href="https://myapps.microsoft.com" target="_blank" rel="noreferrer">
          your Microsoft account&rsquo;s apps
        </a>
        .
      </p>

      <h2>Children</h2>
      <p>Meet With is not intended for anyone under 16, and we do not target them.</p>

      <h2>Changes</h2>
      <p>
        If this policy changes in a way that matters, we will say so here and update
        the date at the top. Continuing to use Meet With after that means the new
        version applies.
      </p>
    </LegalPage>
  );
}
